import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Box, Button, Checkbox, Grid, IconButton } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { ValidationModifierType, ValidationModifierTypeDisplay } from 'parkchirp-shared/enums';
import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { HourlyAccountUserGroupValidation } from '../../types';
import { datagridFilterPanelAttributes, dayjs } from '../../utils';
import { ValidationMethodAutocomplete } from '../inputs/validationMethodAutocomplete';
import { DatagridLinearProgress } from '../loading';

export interface PortalValidationsTableProps {
	facilityId: string;
	validationMethods?: HourlyAccountUserGroupValidation[];
	parentValidationMethods?: HourlyAccountUserGroupValidation[];
	canEdit: boolean;
	canAdd?: boolean;
	onChange: (validations: HourlyAccountUserGroupValidation[]) => void;
}

export const PortalValidationsTable = ({
	facilityId,
	validationMethods,
	parentValidationMethods,
	canEdit,
	canAdd = true,
	onChange,
}: PortalValidationsTableProps) => {
	const validationsMap = useMemo(
		() => Object.fromEntries(validationMethods.map((v) => [v.validationMethod?.id, v])),
		[validationMethods],
	);

	const mergedValidations = useMemo(
		() =>
			parentValidationMethods?.map((parentValidation) => {
				const inputValidation = validationsMap[parentValidation.validationMethod.id];
				return {
					...parentValidation,
					id: inputValidation?.id ?? uuidv4(),
					parentId: parentValidation.id,
					parentQrValidation: parentValidation.qrValidation,
					parentWebValidation: parentValidation.webValidation,
					qrValidation: inputValidation?.qrValidation ?? false,
					webValidation: inputValidation?.webValidation ?? false,
				};
			}) ?? [],
		[validationsMap, parentValidationMethods],
	);

	const renderedValidations = parentValidationMethods ? mergedValidations : validationMethods;

	const handleAddRow = useCallback(() => {
		onChange([
			...validationMethods,
			{
				id: uuidv4(),
				webValidation: false,
				qrValidation: true,
				validationMethod: null,
			},
		]);
	}, [validationMethods, onChange]);

	const selectedValidationIds = useMemo(
		() => validationMethods.map((validation) => String(validation.validationMethod?.id)),
		[validationMethods],
	);

	const handleDeleteRow = useCallback(
		(params) => {
			onChange(validationMethods.filter((row) => row.id !== params.row.id));
		},
		[validationMethods, onChange],
	);

	const handleEditRow = useCallback(
		(params, modification) => {
			onChange(
				renderedValidations.map((row) => {
					if (params.row.id === row.id) {
						return {
							...row,
							...modification,
						};
					}
					return row;
				}),
			);
		},
		[renderedValidations, onChange],
	);

	// @todo strong typing
	const columns: any[] = [
		{
			field: 'name',
			headerName: 'Validation Method',
			width: 300,
			renderCell: (params) => {
				const {
					row: { parentId, validationMethod },
				} = params;

				return canEdit ? (
					<ValidationMethodAutocomplete
						required
						error={null}
						disabled={!canEdit || !!parentId}
						facilityId={facilityId}
						validationMethodId={validationMethod?.id}
						validationMethodText={validationMethod?.text}
						onChange={(method) => {
							let modification = { validationMethod: null };
							if (method) {
								modification = {
									validationMethod: {
										id: method.id,
										text: method.text,
										startsOn: method.startsOn,
										expiresOn: method.expiresOn,
										hourlyValidation: {
											name: method.validation.name,
											modifier: {
												type: method.validation.modifier.type,
												value: method.validation.modifier.value,
												rate: {
													name: method.validation.modifier.rate.name,
												},
											},
										},
									},
								};
							}
							handleEditRow(params, modification);
						}}
						ignoredOptions={selectedValidationIds}
						size='small'
					/>
				) : (
					params.row.validationMethod.text
				);
			},
		},
		{
			field: 'validation',
			headerName: 'Validation',
			sortable: false,
			filterable: false,
			flex: 1,
			renderCell: ({ row: { validationMethod } }) => validationMethod?.hourlyValidation.name,
		},
		{
			field: 'modifierType',
			headerName: 'Modifier Type',
			sortable: false,
			filterable: false,
			width: 125,
			renderCell: ({ row: { validationMethod } }) => {
				return ValidationModifierTypeDisplay[validationMethod?.hourlyValidation.modifier.type];
			},
		},
		{
			field: 'modifierValue',
			headerName: 'Modifier Value',
			sortable: false,
			filterable: false,
			flex: 1,
			renderCell: ({ row: { validationMethod } }) => {
				switch (validationMethod?.hourlyValidation.modifier.type) {
					case ValidationModifierType.RATE:
						return validationMethod.hourlyValidation.modifier.rate?.name;
					case ValidationModifierType.TIME_OFF:
						return `${validationMethod.hourlyValidation.modifier.value} minutes`;
					case ValidationModifierType.DOLLAR_OFF:
						return `$${(validationMethod.hourlyValidation.modifier.value / 100).toFixed(2)}`;
					case ValidationModifierType.PERCENT_OFF:
						return `${Number.parseFloat((validationMethod.hourlyValidation.modifier.value / 10000).toFixed(4))}%`;
					default:
						return '';
				}
			},
		},
		{
			field: 'startsOn',
			headerName: 'Starts On',
			width: 125,
			editable: false,
			renderCell: ({ row: { validationMethod } }) => {
				const startsOn = validationMethod?.startsOn ?? validationMethod?.hourlyValidation.startsOn;
				return startsOn ? dayjs.utc(Number(startsOn)).format('M/D/YY') : '-';
			},
		},
		{
			field: 'expiresOn',
			headerName: 'Expires On',
			width: 125,
			editable: false,
			renderCell: ({ row: { validationMethod } }) => {
				const expiresOn = validationMethod?.expiresOn ?? validationMethod?.hourlyValidation.expiresOn;
				return expiresOn ? dayjs.utc(Number(expiresOn)).format('M/D/YY') : '-';
			},
		},
		{
			field: 'qrValidation',
			headerName: 'QR Code',
			width: 80,
			align: 'center' as const,
			renderCell: (params) => {
				const {
					row: { parentId, parentQrValidation, qrValidation },
				} = params;

				return (
					<Checkbox
						disabled={!canEdit || (parentId && !parentQrValidation)}
						checked={qrValidation}
						onChange={(event) => handleEditRow(params, { qrValidation: event.target.checked })}
					/>
				);
			},
		},
		{
			field: 'webValidation',
			headerName: 'Web',
			width: 80,
			align: 'center' as const,
			renderCell: (params) => {
				const {
					row: { parentId, parentWebValidation, webValidation },
				} = params;

				return (
					<Checkbox
						disabled={!canEdit || (parentId && !parentWebValidation)}
						checked={webValidation}
						onChange={(event) => handleEditRow(params, { webValidation: event.target.checked })}
					/>
				);
			},
		},
		...(canAdd && canEdit
			? [
					{
						field: ' ',
						headerName: '',
						align: 'right' as const,
						width: 40,
						renderCell: (params) => (
							<IconButton onClick={() => handleDeleteRow(params)}>
								<DeleteIcon />
							</IconButton>
						),
					},
				]
			: []),
	];

	return (
		<>
			<Box sx={{ display: 'flex', alignItems: 'center' }}>
				<h4>Validations</h4>
				{canAdd && (
					<Grid container sx={{ marginTop: '5px', justifyContent: 'end' }}>
						<Grid item>
							<Button size='small' onClick={handleAddRow}>
								<AddIcon /> Add Validation
							</Button>
						</Grid>
					</Grid>
				)}
			</Box>
			<DataGridPro
				hideFooter
				sx={{ height: 'auto', minHeight: '92px', mb: canAdd ? 0 : 3 }}
				editMode='row'
				rows={renderedValidations ?? []}
				rowHeight={45}
				onCellEditStop={handleEditRow}
				columnHeaderHeight={45}
				columns={columns}
				slots={{
					loadingOverlay: DatagridLinearProgress,
				}}
				slotProps={{
					filterPanel: {
						sx: {
							'& .MuiDataGrid-filterFormValueInput': { width: 240 },
						},
						...datagridFilterPanelAttributes,
					},
				}}
			/>
		</>
	);
};
